import React, { useCallback, useContext, useEffect, useState } from "react";
import { Inbox, Link2, Sparkles, CheckSquare, Square, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { GateContext } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function UnassignedInbox({ matters = [], onLinked }) {
  const { gate } = useContext(GateContext);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkMatter, setBulkMatter] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState({}); // {evidenceId: matterId}

  const load = useCallback(async () => {
    try { const { data } = await api.get("/evidence/unassigned"); setItems(data); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (items.length === 0) return null;

  const matterName = (id) => matters.find((m) => m.id === id)?.title || "case";
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const linkOne = async (id, matterId) => {
    if (!matterId) { toast.message("Pick a case first"); return; }
    if (!gate()) return;
    try {
      await api.post(`/evidence/${id}/link`, { matter_id: matterId });
      toast.success(`Linked to ${matterName(matterId)}`);
      setSelected((s) => s.filter((x) => x !== id));
      await load(); onLinked && onLinked();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link file"); }
  };

  const bulkLink = async () => {
    if (!bulkMatter) { toast.message("Choose a case to link to"); return; }
    if (selected.length === 0) { toast.message("Select files first"); return; }
    if (!gate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/evidence/bulk-link", { evidence_ids: selected, matter_id: bulkMatter });
      toast.success(`Linked ${data.linked} file${data.linked === 1 ? "" : "s"} to ${data.matter_title}`);
      setSelected([]); setBulkMatter("");
      await load(); onLinked && onLinked();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link files"); }
    finally { setBusy(false); }
  };

  const suggest = async (id) => {
    if (!gate()) return;
    try {
      const { data } = await api.get(`/evidence/${id}/suggest-matter`);
      if (!data.matter_id) { toast.message(data.reason || "No case to suggest"); return; }
      setSuggested((s) => ({ ...s, [id]: data.matter_id }));
      toast.message(`Justice suggests: ${data.matter_title}`, { description: data.reason });
    } catch { toast.error("Could not get a suggestion"); }
  };

  return (
    <section className="border rounded-xl p-4 sm:p-5 mb-6" style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.3)" }} data-testid="unassigned-inbox">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5" style={{ color: "var(--ek-warning)" }} />
          <h3 className="font-head font-semibold tracking-tight">Unassigned files</h3>
          <span className="ek-stamp" data-testid="unassigned-count" style={{ color: "var(--ek-warning)", borderColor: "rgba(245,158,11,0.35)" }}>{items.length}</span>
        </div>
        {selected.length > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-link-bar">
            <span className="ek-mono-label">{selected.length} SELECTED</span>
            <div className="w-44">
              <Select value={bulkMatter} onValueChange={setBulkMatter}>
                <SelectTrigger data-testid="bulk-matter-select" className="bg-transparent h-9"><SelectValue placeholder="Link to case…" /></SelectTrigger>
                <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`bulk-opt-${m.id}`}>{m.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={bulkLink} disabled={busy || !bulkMatter} data-testid="bulk-link-btn" className="rounded-md h-9" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              <Link2 className="h-4 w-4 mr-1.5" /> Link
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {items.map((it) => {
          const on = selected.includes(it.id);
          const sug = suggested[it.id];
          return (
            <div key={it.id} className="flex items-center gap-3 rounded-md border p-2.5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid={`unassigned-row-${it.id}`}>
              <button onClick={() => toggle(it.id)} data-testid={`select-${it.id}`} aria-label="select">
                {on ? <CheckSquare className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> : <Square className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />}
              </button>
              <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
              <span className="text-sm truncate flex-1" data-private>{it.filename}</span>
              <button onClick={() => suggest(it.id)} data-testid={`suggest-${it.id}`} className="hidden sm:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md" style={{ color: "var(--ek-primary)" }}>
                <Sparkles className="h-3.5 w-3.5" /> Ask Justice
              </button>
              <div className="w-40 shrink-0">
                <Select value={sug || ""} onValueChange={(v) => linkOne(it.id, v)}>
                  <SelectTrigger data-testid={`link-select-${it.id}`} className="bg-transparent h-9"><SelectValue placeholder="Link to case…" /></SelectTrigger>
                  <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`link-opt-${it.id}-${m.id}`}>{m.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
